import styled from 'styled-components'
import { Flex, Text } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import useTheme from 'hooks/useTheme'
import { palette } from '../colors'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 56px 0;

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 80px 0;
    gap: 48px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 760px;
  text-align: center;
`

const Eyebrow = styled(Text)`
  font-family: 'Geist Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 2px;
`

const Title = styled(Text)`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1.1;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 48px;
    letter-spacing: -1.5px;
  }
`

const Grid = styled.div`
  display: grid;
  width: 100%;
  max-width: 1280px;
  grid-template-columns: 1fr;
  gap: 16px;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: 1fr 1fr;
  }
`

const TradeCell = styled.div`
  display: contents;

  ${({ theme }) => theme.mediaQueries.md} {
    display: block;
    grid-column: 1 / -1;
  }
`


const Card = styled.div<{ children?: React.ReactNode; $bg: string; $border: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 28px;
  border-radius: 20px;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  box-shadow: 0 4px 24px rgba(10, 26, 51, 0.08);
  overflow: hidden;
`

const WideCard = styled(Card)`
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    align-items: center;
    gap: 28px;
    padding: 32px;
  }
`

const CardCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  align-items: flex-start;
`

const Icon = styled.div<{ children?: React.ReactNode; $color: string }>`
  font-size: 32px;
  line-height: 1;
  color: ${({ $color }) => $color};
`

const CardLink = styled.a<{ children?: React.ReactNode; $color: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>>`
  font-weight: 600;
  font-size: 14px;
  color: ${({ $color }) => $color};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const ChartBox = styled.div<{ children?: React.ReactNode; $bg: string; $border: string }>`
  width: 100%;
  max-width: 320px;
  padding: 18px;
  border-radius: 12px;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 80px;
`

const Bar = styled.div<{ $h: number; $bg: string }>`
  flex: 1;
  height: ${({ $h }) => $h}%;
  background: ${({ $bg }) => $bg};
  border-radius: 3px;
`

const ChainStack = styled.div`
  display: flex;
  align-items: center;
`

const ChainDot = styled.div<{ $color: string; $border: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2px solid ${({ $border }) => $border};
  margin-left: -8px;
  &:first-child {
    margin-left: 0;
  }
`

const AprPill = styled.div<{ children?: React.ReactNode; $card: string; $border: string } & React.HTMLAttributes<HTMLDivElement>>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${({ $card }) => $card};
  border: 1px solid ${({ $border }) => $border};
  font-size: 12px;
  font-weight: 600;
`

const Bento: React.FC = () => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const p = palette(theme.isDark)

  return (
    <Section>
      <Header>
        <Eyebrow color={p.cyan}>BUILT ON QOM</Eyebrow>
        <Title color={p.fg}>{t('Everything you need on QOM Chain')}</Title>
        <Text fontSize="17px" color={p.fgSecondary} style={{ lineHeight: 1.5 }}>
          {t('Swap native and bridged assets, move liquidity across chains and earn — straight from your wallet.')}
        </Text>
      </Header>

      <Grid>
        <TradeCell>
          <WideCard $bg={p.tintTrade} $border={p.border}>
            <CardCopy>
              <Icon $color={p.cyan}>⇄</Icon>
              <Text fontSize="26px" bold color={p.fg} style={{ letterSpacing: '-0.6px', lineHeight: 1.15 }}>
                {t('Trade native QOM pairs')}
              </Text>
              <Text fontSize="15px" color={p.fgSecondary} style={{ lineHeight: 1.5 }}>
                {t('Swap WQOM, qUSDT, qUSDC and more — direct from your wallet, with fees that stay on the chain.')}
              </Text>
              <CardLink
                $color={p.fg}
                href="/swap?inputCurrency=QOM&outputCurrency=0xC276b91c0e8D10260af7b67009d2683bB7776308"
              >
                {t('Open Swap')} →
              </CardLink>
            </CardCopy>
            <ChartBox $bg={p.card} $border={p.border}>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="14px" bold color={p.fg}>
                  QOM / qUSDT
                </Text>
                <Text fontSize="11px" color={p.fgSecondary} style={{ fontFamily: '"Geist Mono", monospace' }}>
                  Live · QL1
                </Text>
              </Flex>
              <Text fontSize="11px" color={p.fgSecondary} style={{ letterSpacing: 1.5 }}>
                NATIVE PAIR
              </Text>
              <Bars>
                <Bar $h={42} $bg={`${p.cobalt}33`} />
                <Bar $h={60} $bg={`${p.cobalt}33`} />
                <Bar $h={50} $bg={`${p.cobalt}33`} />
                <Bar $h={75} $bg={p.cobalt} />
                <Bar $h={64} $bg={p.cobalt} />
                <Bar $h={88} $bg={p.cobalt} />
                <Bar $h={70} $bg={p.cyan} />
                <Bar $h={100} $bg={p.cyan} />
              </Bars>
            </ChartBox>
          </WideCard>
        </TradeCell>

        <Card $bg={p.tintBridge} $border={p.border}>
          <Icon $color={p.cyan}>⇆</Icon>
          <Text fontSize="22px" bold color={p.fg} style={{ letterSpacing: '-0.4px', lineHeight: 1.15 }}>
            {t('Bridge into QOM')}
          </Text>
          <Text fontSize="14px" color={p.fgSecondary} style={{ lineHeight: 1.5 }}>
            {t('Move assets from Ethereum in a few clicks.')}
          </Text>
          <Text fontSize="11px" color="#F0A500" style={{ lineHeight: 1.4, marginTop: '4px' }}>
            {t('Third-party service — use at your own risk.')}
          </Text>
          <ChainStack>
            <ChainDot $color="#F0B90B" $border={p.tintBridge} />
            <ChainDot $color="#627EEA" $border={p.tintBridge} />
            <ChainDot $color={p.cobalt} $border={p.tintBridge} />
            <ChainDot $color={p.cyan} $border={p.tintBridge} />
          </ChainStack>
          <CardLink $color={p.fg} href="https://bridge.qom.one" target="_blank" rel="noopener noreferrer">
            {t('Open Bridge')} →
          </CardLink>
        </Card>

        <Card $bg={p.tintEarn} $border={p.border}>
          <Icon $color={p.cyan}>$</Icon>
          <Text fontSize="22px" bold color={p.fg} style={{ letterSpacing: '-0.4px', lineHeight: 1.15 }}>
            {t('Earn from liquidity')}
          </Text>
          <Text fontSize="14px" color={p.fgSecondary} style={{ lineHeight: 1.5 }}>
            {t('Provide liquidity to QOM pools and earn a share of swap fees.')}
          </Text>
          <AprPill $card={p.card} $border={p.border} style={{ color: p.fg }}>
            <span style={{ letterSpacing: 1, fontSize: 11, color: p.fgSecondary }}>FEE SHARE</span>
            <span style={{ color: p.success, fontWeight: 700 }}>0.17%</span>
          </AprPill>
          <CardLink $color={p.fg} href="/liquidity">
            {t('Add liquidity')} →
          </CardLink>
        </Card>
      </Grid>
    </Section>
  )
}

export default Bento
